import React, { useEffect, useState } from 'react';
import axios from 'axios';

const TextArea = ({ label, value, onChange, placeholder }) => (
  <div className="mb-6">
    <label className="block text-sm font-medium mb-2 text-gray-700 dark:text-gray-300">{label}</label>
    <textarea
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      className="w-full min-h-[120px] p-3 rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500"
    />
  </div>
);

const GlobalAIConfig = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [settings, setSettings] = useState({});
  const [prompts, setPrompts] = useState({ salesassist: '', assistant: '', personal_assistant: '' });

  const fetchConfig = async () => {
    setLoading(true);
    setError('');
    try {
      const res = await axios.get('/api/ai-config/global');
      const data = res.data?.data || {};
      setSettings(data.settings || {});
      setPrompts({
        salesassist: data.prompts?.salesassist || data.prompts?.power_sales || '',
        assistant: data.prompts?.assistant || '',
        personal_assistant: data.prompts?.personal_assistant || ''
      });
    } catch (e) {
      setError(e.response?.data?.error || 'Failed to load Global AI config');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchConfig();
  }, []);

  const handleSaveAll = async () => {
    setSaving(true);
    setError('');
    setSuccess('');
    try {
      await axios.put('/api/ai-config/global', { settings, prompts });
      setSuccess('Global AI settings and prompts saved');
    } catch (e) {
      setError(e.response?.data?.error || 'Failed to save Global AI config');
    } finally {
      setSaving(false);
    }
  };

  const handleSavePrompt = async (key) => {
    setSaving(true);
    setError('');
    setSuccess('');
    try {
      await axios.put(`/api/ai-config/global/prompts/${key}`, { prompt: prompts[key] });
      setSuccess(`Saved prompt for ${key}`);
    } catch (e) {
      setError(e.response?.data?.error || `Failed to save prompt for ${key}`);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="p-6 bg-white dark:bg-gray-900 rounded-lg shadow">
        <div className="animate-pulse h-4 w-32 bg-gray-300 dark:bg-gray-700 rounded mb-4" />
        <div className="animate-pulse h-4 w-64 bg-gray-300 dark:bg-gray-700 rounded" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="bg-white dark:bg-gray-900 rounded-lg shadow p-6">
        <div className="mb-4">
          <h2 className="text-xl font-semibold text-gray-900 dark:text-white">Global AI Configuration</h2>
          <p className="text-sm text-gray-600 dark:text-gray-400">Common prompts are prepended to each tenant's system prompt for the agent.</p>
        </div>

        {error && (
          <div className="mb-4 p-3 rounded border border-red-300 bg-red-50 text-red-700">{error}</div>
        )}
        {success && (
          <div className="mb-4 p-3 rounded border border-green-300 bg-green-50 text-green-700">{success}</div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div>
            <TextArea
              label="SalesAssist - Global Common Prompt"
              value={prompts.salesassist}
              onChange={(val) => setPrompts((p) => ({ ...p, salesassist: val }))}
              placeholder="Guidelines applied to SalesAssist across all tenants..."
            />
            <button
              onClick={() => handleSavePrompt('salesassist')}
              disabled={saving}
              className="px-4 py-2 text-sm rounded bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50"
            >
              {saving ? 'Saving...' : 'Save Power Sales Prompt'}
            </button>
          </div>

          <div>
            <TextArea
              label="CRM Assistant - Global Common Prompt"
              value={prompts.assistant}
              onChange={(val) => setPrompts((p) => ({ ...p, assistant: val }))}
              placeholder="Guidelines applied to CRM Assistant across all tenants..."
            />
            <button
              onClick={() => handleSavePrompt('assistant')}
              disabled={saving}
              className="px-4 py-2 text-sm rounded bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50"
            >
              {saving ? 'Saving...' : 'Save CRM Assistant Prompt'}
            </button>
          </div>

          <div>
            <TextArea
              label="Personal Assistant - Global Common Prompt"
              value={prompts.personal_assistant}
              onChange={(val) => setPrompts((p) => ({ ...p, personal_assistant: val }))}
              placeholder="Guidelines applied to Personal Assistant across all tenants..."
            />
            <button
              onClick={() => handleSavePrompt('personal_assistant')}
              disabled={saving}
              className="px-4 py-2 text-sm rounded bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50"
            >
              {saving ? 'Saving...' : 'Save Personal Assistant Prompt'}
            </button>
          </div>
        </div>

        <div className="mt-8">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">Global Settings (subset)</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium mb-1 text-gray-700 dark:text-gray-300">Default Agent</label>
              <select
                value={settings.default_agent || ''}
                onChange={(e) => setSettings((s) => ({ ...s, default_agent: e.target.value }))}
                className="w-full p-2 rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100"
              >
                <option value="openai">OpenAI</option>
                <option value="gemini">Gemini</option>
                <option value="openrouter">OpenRouter</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium mb-1 text-gray-700 dark:text-gray-300">Max RAG Results</label>
              <input
                type="number"
                value={settings.max_rag_results || 8}
                onChange={(e) => setSettings((s) => ({ ...s, max_rag_results: parseInt(e.target.value || '0', 10) }))}
                className="w-full p-2 rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100"
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1 text-gray-700 dark:text-gray-300">Min Relevance Score</label>
              <input
                type="number"
                step="0.01"
                value={settings.min_relevance_score || 0.7}
                onChange={(e) => setSettings((s) => ({ ...s, min_relevance_score: parseFloat(e.target.value || '0') }))}
                className="w-full p-2 rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100"
              />
            </div>
          </div>

          <div className="mt-4">
            <button
              onClick={handleSaveAll}
              disabled={saving}
              className="px-5 py-2 rounded bg-green-600 text-white hover:bg-green-700 disabled:opacity-50"
            >
              {saving ? 'Saving...' : 'Save All Changes'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default GlobalAIConfig;


